import { useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { getUser, setUser, createAlert } from "~/store"
import { getLocations } from "~/utils/Locations"
import Sidebar from "~/components/Sidebar/Sidebar"
import Footer from "~/components/Footer/Footer"
import Heading from "~/components/Heading/Heading"
import Button from "~/components/Button/Button"
import TextField from "~/components/TextField/TextField"
import Checkbox from "~/components/Checkbox/Checkbox"
import Slider from "~/components/Slider/Slider"
import Dropdown from "~/components/Dropdown/Dropdown"
import SettingsMenu from "~/components/SettingsMenu/SettingsMenu"
import PageContent from "~/components/PageContent/PageContent"
import PageWithSidebar from "~/components/PageWithSidebar/PageWithSidebar"
import Api from "~/utils/Api"

import "./SettingsProfile.scss"
import { IconHarvest } from "~/components/Icons/IconHarvest"
import { IconAiAssistant } from "~/components/Icons/IconAiAssistant"
import McpConnectionPanel from "~/components/McpConnectionPanel/McpConnectionPanel"

function SettingsProfile() {
  const user = useSelector(getUser)
  const dispatch = useDispatch()
  const [updating, setUpdating] = useState(false)
  const [luxaforToken, setLuxaforToken] = useState(user?.luxafor_token || "")
  const [location, setLocation] = useState(user?.location)
  const [alert, setAlert] = useState(user?.alert)
  const [alertVolume, setAlertVolume] = useState(user?.alert_volume)
  const [mcpEnabled, setMcpEnabled] = useState(false)

  const updateUser = () => {
    setUpdating(true)

    const params = {
      user: {
        luxafor_token: luxaforToken,
        location: location,
        alert: alert,
        alert_volume: alertVolume,
      },
    }

    Api.put(`/users/${user?.id}`, params)
      .then((response) => {
        const userData = response.data["data"]["attributes"]
        dispatch(setUser(userData))
        dispatch(
          createAlert({
            message: "User settings updated",
            type: "success",
          }),
        )
      })
      .catch((error) => {
        dispatch(
          createAlert({
            message: error.message,
            type: "error",
          }),
        )
      })
      .then(() => {
        setUpdating(false)
      })
  }

  return (
    <PageWithSidebar>
      <PageContent center={false}>
        <div className="SettingsProfile">
          <Heading text="Settings" />

          <div className="SettingsProfile__content">
            <SettingsMenu />
            <div className="SettingsProfile__form">
              <h2 className="SettingsProfile__form-title">Account details</h2>
              <TextField
                label="Luxafor ID"
                placeholder="E.g. 8844554466"
                value={luxaforToken}
                valueChanged={setLuxaforToken}
                regex={/[&/\\#,$~%.":*{}]/g}
              />
              <Dropdown
                ariaLabel="Location dropdown"
                label="Location"
                value={user?.location || ""}
                options={getLocations("Select a location...")}
                valueChanged={(newValue) => setLocation(newValue)}
              />
              {!user?.location && (
                <span className="SettingsProfile__location_warning">
                  ⚠️ Please select your location
                </span>
              )}
              <Checkbox
                label="Alert"
                id="switch-alert"
                value={Boolean(user?.alert)}
                valueChanged={(newValue) => setAlert(newValue)}
              />
              <Slider
                label="Alert volume"
                value={user?.alert_volume ?? 0.5}
                valueChanged={(newValue) => setAlertVolume(newValue)}
                disabled={!alert}
              />
              <div className="SettingsProfile__form-actions">
                <Button
                  text={updating ? "Updating..." : "Update"}
                  style="primary"
                  size="small"
                  onClick={updateUser}
                  disabled={updating}
                />
              </div>

              <h2 className="SettingsProfile__form-title">Integrations</h2>
              <div className="SettingsProfile__integration-row">
                <IconHarvest className="SettingsProfile__integration-icon SettingsProfile__integration-icon--harvest" />

                <h3 className="SettingsProfile__integration-label">Harvest</h3>

                <p className="SettingsProfile__integration-description">
                  Connect your Harvest account to control your tracker from
                  pomodoro spaces.
                </p>

                <Checkbox
                  id="harvest_integration"
                  value={Boolean(user?.has_harvest_access)}
                  valueChanged={async (enable) => {
                    if (enable) {
                      window.location.href = "/harvest_session/new"
                    } else {
                      await Api.delete("../harvest_session")
                      dispatch(setUser({ ...user, has_harvest_access: false }))
                    }
                  }}
                />
              </div>

              <div className="SettingsProfile__integration-row">
                <IconAiAssistant className="SettingsProfile__integration-icon SettingsProfile__integration-icon--mcp" />

                <h3 className="SettingsProfile__integration-label">
                  AI assistant
                </h3>

                <p className="SettingsProfile__integration-description">
                  Let an MCP-compatible AI assistant start and stop your timers
                  and tell you when to take a break.
                </p>

                <Checkbox
                  id="mcp_integration"
                  value={mcpEnabled}
                  valueChanged={setMcpEnabled}
                />
              </div>
              {mcpEnabled && <McpConnectionPanel />}
            </div>
          </div>
        </div>
        <Footer style="light" />
      </PageContent>
      <Sidebar style="primary" />
    </PageWithSidebar>
  )
}

export default SettingsProfile
